import React from 'react';
import { useTranslation } from 'react-i18next';
// Its own one-key namespace, never `../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import './ObservabilitySkeleton.css';

/** Placeholder rows, as many as the section draws while its first page loads. */
const ROWS = [0, 1, 2, 3, 4, 5];
/** The two sections' tabs: conversations and process runs. */
const TABS = [0, 1];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`observability-skeleton__bone ${className}`} />
);

export interface ObservabilitySkeletonProps {
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
}

/**
 * Loading shape of the agent's log (SL-1947). `RouteSkeleton` (inside the app
 * chrome), the agent layout while the agent loads and the route's Suspense
 * fallback draw this; the page then mounts with the same card, and its section
 * draws its own rows while the first page of runs loads.
 *
 * The page as it arrives: breadcrumb, then the page card — the module's title
 * and its line below, the two sections' tabs, the toolbar (search and filters)
 * and the table: a header row, then the runs.
 *
 * Its own classes, never the page's: the page's sheets come with its chunk and
 * this paints before it. Nothing visible but bones; the status carries its
 * translated text inside, visually hidden, so a screen reader announces it. No
 * `aria-busy`: on a live region it tells assistive tech to hold the
 * announcement.
 */
export const ObservabilitySkeleton: React.FC<ObservabilitySkeletonProps> = ({
  standalone = false,
}) => {
  const { t } = useTranslation(NS);
  const text = t('loading');

  return (
    <div
      className={`observability-skeleton${standalone ? ' observability-skeleton--standalone' : ''}`}
      role="status"
      data-testid="observability-skeleton"
    >
      <span className="observability-skeleton__sr-only">{text}</span>
      <div className="observability-skeleton__header" aria-hidden="true">
        <Bone className="observability-skeleton__crumb" />
      </div>
      <div className="observability-skeleton__card" aria-hidden="true">
        <div className="observability-skeleton__heading">
          <Bone className="observability-skeleton__title" />
          <Bone className="observability-skeleton__line observability-skeleton__line--subtitle" />
        </div>
        <div className="observability-skeleton__tabs">
          {TABS.map((tab) => (
            <Bone key={tab} className="observability-skeleton__tab" />
          ))}
        </div>
        <div className="observability-skeleton__toolbar">
          <Bone className="observability-skeleton__search" />
          <div className="observability-skeleton__filters">
            <Bone className="observability-skeleton__filter" />
            <Bone className="observability-skeleton__filter" />
          </div>
        </div>
        <div className="observability-skeleton__table">
          <div className="observability-skeleton__row observability-skeleton__row--head">
            <Bone className="observability-skeleton__cell observability-skeleton__cell--status" />
            <Bone className="observability-skeleton__cell" />
            <Bone className="observability-skeleton__cell" />
            <Bone className="observability-skeleton__cell" />
          </div>
          {ROWS.map((row) => (
            <div key={row} className="observability-skeleton__row">
              <Bone className="observability-skeleton__cell observability-skeleton__cell--status" />
              <Bone className="observability-skeleton__cell" />
              <Bone className="observability-skeleton__cell" />
              <Bone className="observability-skeleton__cell" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
